import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Input } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import { ReturnList, ReturnForm } from '@/components/organisms/Return';
import {
  useReturnsList,
  useApproveReturnMutation,
  useRejectReturnMutation,
  useMostReturnedProductsQuery,
} from '@/hooks/useErpApi';
import {
  setReturns,
  setFilters,
  clearFilters,
  updateReturnStatus,
  selectFilteredReturns,
  selectReturnsStats,
  type ReturnStatus,
} from '@/features/returns';
import type { AppDispatch } from '@/store';
import { formatCurrency } from '@/utils/formatters';

export default function ReturnsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [activeTab, setActiveTab] = useState<'all' | ReturnStatus>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedReturnId, setSelectedReturnId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  const filteredReturns = useSelector(selectFilteredReturns);
  const stats = useSelector(selectReturnsStats);

  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const {
    data: returns,
    isLoading,
    error,
    refetch,
  } = useReturnsList({
    status: activeTab !== 'all' ? activeTab : undefined,
    limit: itemsPerPage,
    offset,
  });
  
  // Reset to page 1 when tab changes
  const handleTabChange = (tab: 'all' | ReturnStatus) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const { data: mostReturnedProducts } = useMostReturnedProductsQuery({
    limit: 5,
  });

  const { mutate: approveReturn, isLoading: isApproving } = useApproveReturnMutation();
  const { mutate: rejectReturn, isLoading: isRejecting } = useRejectReturnMutation();

  useEffect(() => {
    if (returns) {
      dispatch(setReturns(returns));
    }
  }, [returns, dispatch]);

  useEffect(() => {
    dispatch(setFilters({ search: searchQuery }));
  }, [searchQuery, dispatch]);

  const handleApprove = (returnId: string) => {
    setSelectedReturnId(returnId);
    setIsApproveModalOpen(true);
  };

  const handleReject = (returnId: string) => {
    setSelectedReturnId(returnId);
    setIsRejectModalOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedReturnId) return;

    try {
      await approveReturn({ id: selectedReturnId, notes: approvalNotes });
      dispatch(updateReturnStatus({ id: selectedReturnId, status: 'approved' }));
      setIsApproveModalOpen(false);
      setApprovalNotes('');
      setSelectedReturnId(null);
      refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to approve return:', error);
      alert('Failed to approve return. Please try again.');
    }
  };

  const confirmReject = async () => {
    if (!selectedReturnId || !rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await rejectReturn({ id: selectedReturnId, reason: rejectReason });
      dispatch(updateReturnStatus({ id: selectedReturnId, status: 'rejected' }));
      setIsRejectModalOpen(false);
      setRejectReason('');
      setSelectedReturnId(null);
      refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to reject return:', error);
      alert('Failed to reject return. Please try again.');
    }
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    refetch();
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);

    if (newDateRange.start && newDateRange.end) {
      dispatch(setFilters({ dateRange: newDateRange }));
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDateRange({ start: '', end: '' });
    setActiveTab('all');
    dispatch(clearFilters());
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <Icon name="CircleAlert" className="mx-auto h-12 w-12 text-red-600 mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Failed to Load Returns</h3>
          <p className="text-red-700 mb-4">{error.message}</p>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Returns Management</h1>
          <p className="text-gray-600 mt-1">Manage product returns and refunds</p>
        </div>
        <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
          <Icon name="Plus" className="mr-2 h-4 w-4" />
          Create Return
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Returns</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalReturns}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Package" className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalReturnsThisMonth}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pendingCount}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Refunded</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(stats.totalRefundThisMonth)}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Search</label>
            <Input
              placeholder="Search by Return ID or Sale ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Icon name="Search" size={16} />}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-between items-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'pending'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'approved'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setActiveTab('refunded')}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'refunded'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Refunded
            </button>
            <button
              onClick={() => handleTabChange('rejected')}
              className={`px-4 py-2 rounded-lg ${
                activeTab === 'rejected'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rejected
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Returns List */}
      <div className="bg-white rounded-lg shadow">
        <ReturnList
          returns={filteredReturns}
          isLoading={isLoading}
          onApprove={handleApprove}
          onReject={handleReject}
          onView={(id) => navigate(`/returns/${id}`)}
        />
      </div>

      {/* Most Returned Products */}
      {mostReturnedProducts && mostReturnedProducts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Returned Products</h3>
          <div className="space-y-3">
            {mostReturnedProducts.map((product, index) => (
              <div key={index} className="flex justify-between items-center border-b pb-2">
                <div>
                  <p className="font-medium text-gray-900">{product.product_name}</p>
                  <p className="text-sm text-gray-500">{product.total_returned} returns</p>
                </div>
                <p className="font-semibold text-gray-900">
                  {formatCurrency(product.return_amount)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Return Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Return"
        size="lg"
      >
        <ReturnForm onSuccess={handleCreateSuccess} onCancel={() => setIsCreateModalOpen(false)} />
      </Modal>

      {/* Approve Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="Approve Return"
      >
        <div className="space-y-4">
          <p className="text-gray-700">Are you sure you want to approve this return?</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval Notes (Optional)
            </label>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes about this approval"
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={() => setIsApproveModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmApprove} disabled={isApproving}>
              {isApproving ? 'Approving...' : 'Approve Return'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Reject Return"
      >
        <div className="space-y-4">
          <p className="text-gray-700">Are you sure you want to reject this return?</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Explain why this return is being rejected"
              required
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={() => setIsRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="error"
              onClick={confirmReject}
              disabled={isRejecting || !rejectReason.trim()}
            >
              {isRejecting ? 'Rejecting...' : 'Reject Return'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
